/**
 * Iter 83.1 — Notifications Bell (Phase 5)
 *
 * Drop-in replacement for the legacy stub. Backed by the Iter 83.1
 * notification API (8 endpoints, owner-scoped). Visual DNA inherited
 * from ShareModal / PlanExportModal / AccountSettingsModal: dark
 * #0a0a0b foundation, ForgedOps orange accents, 44px touch targets,
 * uppercase mono section headers, sticky action zones.
 *
 * Features delivered in 83.1:
 *  - Unread badge in topbar (live every 30s).
 *  - Slide-down panel: filter pills (unread/all/archived).
 *  - Single-item: mark-read · archive · deep-link navigation.
 *  - Bulk: read-all · archive-all.
 *  - Priority indicator dot (orange/red bar for high/urgent).
 *  - Mono timestamp; relative formatting via formatRelative.
 *  - Empty-state copy matches platform tone.
 *
 * Explicitly deferred to 83.2: mentions surface, digest preview, search
 * integration in the bell.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Bell, Check, CheckCheck, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/utils";

const PRIORITY_BAR = {
  urgent: "var(--st-escalated, #ef4444)",
  high:   "#fb923c",
  normal: "var(--fop-primary, #ff6a1a)",
  low:    "var(--fop-text-3, #6b7280)",
};

const TYPE_ICON = {
  "punch.created":           "○",
  "punch.assigned":          "◉",
  "punch.updated":           "✎",
  "punch.ready_for_review":  "▸",
  "punch.rejected":          "✕",
  "punch.closed":            "✓",
  "export.completed":        "↓",
  "export.failed":           "!",
  "share.created":           "↗",
  "share.revoked":           "⊘",
  "markup.created":          "✏",
  "object.created":          "＋",
  "object.updated":          "✎",
  "mention":                 "@",
};

const FILTER_LABELS = [
  { key: "unread",   label: "Unread"   },
  { key: "all",      label: "All"      },
  { key: "archived", label: "Archived" },
];

// Iter 83.2A — highlight `@username` tokens in the body so mentions
// visually pop out from regular notification text. Pure string→JSX
// transform — no anchor links yet (those land in 83.2B when we add the
// user-resolver-by-handle endpoint).
const MENTION_RE = /(@[A-Za-z0-9][A-Za-z0-9._-]*)/g;
function renderMentions(text) {
  if (!text) return null;
  const parts = text.split(MENTION_RE);
  return parts.map((p, i) =>
    p.startsWith("@") ? (
      <span
        key={i}
        className="font-medium"
        style={{ color: "var(--fop-primary, #ff6a1a)" }}
        data-testid="mention-token"
      >
        {p}
      </span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("unread");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.unread || 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        params: { status: filter, limit: 50 },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Initial badge + 30s background poll.
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  // Refresh list when opened or filter changes.
  useEffect(() => {
    if (open) fetchList();
  }, [open, filter, fetchList]);

  // Click-outside.
  useEffect(() => {
    const click = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const handleOpenItem = async (n) => {
    if (!n.read) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
      } catch { /* silent */ }
    }
    setOpen(false);
    if (n.deep_link) navigate(n.deep_link);
    fetchUnread();
    fetchList();
  };

  const handleArchive = async (e, n) => {
    e.stopPropagation();
    try {
      await api.patch(`/notifications/${n.id}/archive`);
    } catch { /* silent */ }
    fetchUnread();
    fetchList();
  };

  const handleReadAll = async () => {
    try {
      await api.patch("/notifications/read-all");
    } catch { /* silent */ }
    fetchUnread();
    fetchList();
  };

  const handleArchiveAll = async () => {
    try {
      await api.patch("/notifications/archive-all");
    } catch { /* silent */ }
    fetchUnread();
    fetchList();
  };

  return (
    <div ref={ref} className="relative">
      {/* Bell trigger — 44px touch target, sits beside Account/Logout */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Notifications"
        className="h-11 w-11 flex items-center justify-center rounded-md
                   text-[var(--fop-text-2)] hover:text-[var(--fop-text)]
                   hover:bg-[var(--fop-surface-2)]
                   transition-colors relative"
        data-testid="notifications-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full
                       text-[9px] font-bold flex items-center justify-center
                       font-mono"
            style={{
              background: "var(--fop-primary, #ff6a1a)",
              color: "var(--fop-bg, #0a0a0b)",
            }}
            data-testid="notifications-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] fop-surface-2
                     rounded-lg shadow-2xl z-50 fade-up
                     border border-[var(--fop-border)]"
          data-testid="notifications-panel"
        >
          {/* Header — sticky-ish, uppercase mono header treatment */}
          <div className="flex items-center justify-between px-4 py-3
                          border-b border-[var(--fop-border)]">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em]
                              text-[var(--fop-text-3)]">
                Inbox
              </div>
              <div className="text-sm font-medium text-[var(--fop-text)]"
                   data-testid="notifications-unread-summary">
                {unread} unread
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleReadAll}
                disabled={unread === 0}
                className="btn btn-ghost text-[11px] uppercase tracking-wider
                           disabled:opacity-40 disabled:cursor-not-allowed
                           min-h-[36px] px-2"
                data-testid="notifications-read-all"
                title="Mark all read"
              >
                <CheckCheck size={13} /> Read
              </button>
              <button
                onClick={handleArchiveAll}
                className="btn btn-ghost text-[11px] uppercase tracking-wider
                           min-h-[36px] px-2"
                data-testid="notifications-archive-all"
                title="Archive all"
              >
                <Archive size={13} /> Clear
              </button>
            </div>
          </div>

          {/* Filter pills */}
          <div className="flex gap-1 px-3 py-2 border-b border-[var(--fop-border)]">
            {FILTER_LABELS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`text-[11px] uppercase tracking-wider font-mono
                            px-2.5 py-1 rounded transition-colors
                            ${filter === f.key
                              ? "bg-[var(--fop-primary,#ff6a1a)] text-[var(--fop-bg,#0a0a0b)]"
                              : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface)]"}`}
                data-testid={`notifications-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* List */}
          <ul className="max-h-[460px] overflow-y-auto"
              data-testid="notifications-list">
            {loading ? (
              <li className="px-4 py-8 text-center text-sm
                              text-[var(--fop-text-3)]">
                Loading…
              </li>
            ) : items.length === 0 ? (
              <li className="px-4 py-10 text-center text-sm
                              text-[var(--fop-text-3)]"
                  data-testid="notifications-empty">
                <Inbox size={20} className="mx-auto mb-2 opacity-50" />
                <div className="font-mono uppercase tracking-wider text-[10px]
                                text-[var(--fop-text-4)] mb-1">
                  No notifications
                </div>
                <div className="text-xs">
                  {filter === "unread"
                    ? "You're all caught up."
                    : filter === "archived"
                    ? "Nothing archived yet."
                    : "Activity will appear here."}
                </div>
              </li>
            ) : (
              items.map((n) => {
                const icon = TYPE_ICON[n.notification_type] || "•";
                const bar = PRIORITY_BAR[n.priority] || PRIORITY_BAR.normal;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => handleOpenItem(n)}
                      className={`w-full text-left px-3 py-2.5
                                  border-b border-[var(--fop-border)]
                                  hover:bg-[var(--fop-surface)]
                                  transition-colors flex gap-2.5 min-h-[64px]
                                  ${n.read ? "opacity-65" : ""}`}
                      data-testid={`notification-${n.id}`}
                    >
                      {/* Priority bar */}
                      <div className="w-[3px] self-stretch rounded-full shrink-0"
                           style={{ background: bar }} />
                      {/* Icon */}
                      <div className="w-6 h-6 shrink-0 rounded
                                      bg-[var(--fop-surface)]
                                      font-mono text-xs
                                      text-[var(--fop-text-2)]
                                      flex items-center justify-center mt-0.5">
                        {icon}
                      </div>
                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[var(--fop-text)]
                                        font-medium truncate">
                          {n.title}
                        </div>
                        {n.body && (
                          <div className="text-xs text-[var(--fop-text-2)]
                                          truncate mt-0.5">
                            {renderMentions(n.body)}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-mono uppercase
                                            tracking-wider
                                            text-[var(--fop-text-4)]">
                            {n.notification_type?.replace(".", " · ")}
                          </span>
                          <span className="text-[10px] font-mono
                                            text-[var(--fop-text-4)]">
                            · {formatRelative(n.created_at)}
                          </span>
                        </div>
                      </div>
                      {/* Archive single */}
                      {!n.archived && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleArchive(e, n)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              handleArchive(e, n);
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100
                                     hover:opacity-100 self-start
                                     w-7 h-7 flex items-center justify-center
                                     rounded text-[var(--fop-text-3)]
                                     hover:text-[var(--fop-text)]
                                     hover:bg-[var(--fop-surface-2)]
                                     transition-all"
                          data-testid={`notification-archive-${n.id}`}
                          title="Archive"
                        >
                          <Check size={13} />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
